import { reflectKeys } from "@utils/key.reflactor.ts";

export const NAMESPACE = {
  ANNOTATION: "annotation",
  AUTH: "auth",
  APP: "app",
  RESOURCE: "attempt/resource",
  EDITOR_LESSON: "editor/lesson",
  EDITOR_COMPONENT: "editor/component",
} as const;

const ANNOTATION_TYPES = [
  "TEXT_ANNOTATION",
  "IMAGE_ANNOTATION",
  "ACTIVITY_NOTE",
  "TOOLTIP_ANNOTATION",
  "TIMESTAMP_ANNOTATION",
];
export type AnnotationType = (typeof ANNOTATION_TYPES)[number];
export const ANNOTATION_TYPE = reflectKeys(ANNOTATION_TYPES);

const ANNOTATION_RESOURCE_TYPES = [
  "LESSON_ACTIVITY",
  "QUESTION_ANSWER",
  "LESSON_COMPONENT",
  "QUESTION_ANSWER_SNAPSHOT",
];
export type AnnotationResourceType = (typeof ANNOTATION_RESOURCE_TYPES)[number];
export const ANNOTATION_RESOURCE_TYPE = reflectKeys(ANNOTATION_RESOURCE_TYPES);

const ANNOTATION_CONTEXT_TYPES = ["ATTEMPT", "EDITOR"];
export type AnnotationContextType = (typeof ANNOTATION_CONTEXT_TYPES)[number];
export const ANNOTATION_CONTEXT_TYPE = reflectKeys(ANNOTATION_CONTEXT_TYPES);

const ANNOTATION_CONTEXT_MODES = ["VIEW", "EDIT"];
export type AnnotationContextMode = (typeof ANNOTATION_CONTEXT_MODES)[number];
export const ANNOTATION_CONTEXT_MODE = reflectKeys(ANNOTATION_CONTEXT_MODES);

export const ANNOTATION_SNAPSHOT_ACCORDION_LABEL = {
  latestDraft: "Latest Draft",
  pastDrafts: "Past Drafts",
};

export const HIGHLIGHTER_SELECTOR_TYPE = reflectKeys([
  "TEXT_POSITION_SELECTOR",
  "DATA_POSITION_SELECTOR",
  "FRAGMENT_SELECTOR",
]);

export const ANNOTATION_POPUP_EVENT = {
  ON_UPDATE_REF_ELEM: "ON_UPDATE_REF_ELEM",
  ON_NOTE_EDIT: "ON_NOTE_EDIT",
  ON_NOTE_EDIT_IN_DRAWER: "ON_NOTE_EDIT_IN_DRAWER",
  ON_NOTE_SAVE_SUCCESS: "ON_NOTE_SAVE_SUCCESS",
  ON_NOTE_SAVE_FAIL: "ON_NOTE_SAVE_FAIL",
  ON_NOTE_SAVE: "ON_NOTE_SAVE",
  ON_NOTE_CANCEL: "ON_NOTE_CANCEL",
  ON_NOTE_POPUP_CLOSED: "ON_NOTE_POPUP_CLOSED",
} as const;

export const ANNOTATION_MODE = reflectKeys(["EDIT", "INLINE_EDIT", "CREATE", "VIEW", "DELETE"]);

export const ANNOTATION_SELECTOR = {
  TEXT_ANNOTATION: "text-annotation",
  IMAGE_ANNOTATION: "image-annotation",
  ASSESSMENT: "assessment",
  TIMESTAMP_ANNOTATION: "timestamp-annotation",
};

export const ASSESSMENT_SELECTOR = {
  assess1: "assess-1",
  assess2: "assess-2",
  assess3: "assess-3",
};

export const ANNOTATION_COLOR = {
  highlight1: "highlight-1",
  highlight2: "highlight-2",
  highlight3: "highlight-3",
};

export const ANNOTATION_SHAPE = {
  shape1: "shape-1",
  shape2: "shape-2",
  shape3: "shape-3",
};

export const ANNOTATION_COLOR_ARRAY = ["highlight-1", "highlight-2", "highlight-3"];

export const ANNOTATION_SHAPE_ARRAY = ["shape-1", "shape-2", "shape-3"];

export const ASSESSMENT_SELECTOR_ARRAY = ["assess-1", "assess-2", "assess-3"];

export const IMAGE_ANNOTATION_RADIUS = 16;

export const COLUMN_TYPE = reflectKeys([
  "CONTAINER_SINGLE_COLUMN",
  "CONTAINER_TWO_COLUMN",
  "CONTAINER_THREE_COLUMN",
  "CONTAINER_ACCORDION",
]);

const OWNER_TYPES = ["USER", "TEAM"];
export type OwnerType = (typeof OWNER_TYPES)[number];
export const OWNER_TYPE = reflectKeys(OWNER_TYPES);

export const ANNOTATION_ANCHOR_ACTION = reflectKeys(["SCROLL", "EDIT"]);

export const ANCHOR_ADDITIONAL_OFFSET = {
  STICKY_PAGE_NAVIGATOR: 112,
  STICKY_ANNOTATION_ACCORDION_TOGGLE: 56,
  ASSIGNMENT_PAUSE_BANNER: 40,
  PREVIEW_AS_STUDENT_BANNER: 40,
  TEAM_RESPONSE_BANNER: 30,
  OTHER_VIEW_BAR: 60,
  GAMIFICATION_ACHIEVEMENT_BAR: 8,
  LEFT_ACTIVITY_NAV: 54,
  LINKED_ASSIGNMENT_BANNER: 40,
  EDITOR_BANNER: 40,
};

export const ANNOTATION_ACTION = {
  HIGHLIGHT: "highlight",
  HIGHLIGHT_WITH_NOTE: "highlightWithNote",
  DEFINE: "define",
  RECORD: "record",
  DRAW: "draw",
};

export const ANCHORING_CONTEXT_TYPE = {
  ANNOTATION: "ANNOTATION",
  CONVERSATION_CONTEXT: "CONVERSATION_CONTEXT",
};

export const LAC_STATUS = reflectKeys(["PROCESSING", "READY", "ERROR"]);

export const ALPHABETS_LIST = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

export const DRAFT_ANNOTATION_UUID = "newAnnotation";

export const IMAGE_ANNOTATION_TOOL = {
  SELECT: "select",
  DRAW: "draw",
  STAMP: "stamp",
  TEXT: "text",
} as const;

/** Gap (px) between the image annotation config popup and the canvas right/bottom edges. */
export const IMAGE_ANNOTATION_POPUP_OFFSET = 8;

/** Default pen thickness (px) of the image annotation draw tool. */
export const IMAGE_ANNOTATION_DEFAULT_THICKNESS = 5;
